import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { CreditCard, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Import admin card components
import {
  TrialPeriodCard,
  ExtraTicketPriceCard,
  SubscriptionPlansCard,
  TokenInstructionsCard
} from '@/components/admin';

export default function SubscriptionConfigPage() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Solo root può modificare, admin può solo visualizzare
  const isRoot = user?.role === 'root';
  const readOnly = !isRoot;

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [configRes, instrRes] = await Promise.all([
        adminAPI.getSubscriptionConfig(),
        adminAPI.getTokenInstructions()
      ]);
      setConfig(configRes.data);
      setInstructions(instrRes.data.instructions || '');
    } catch (err) {
      toast.error('Errore nel caricamento configurazione');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (readOnly) {
      toast.error('Solo gli utenti root possono modificare i piani');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.updateSubscriptionConfig({
        trial_days: config.trial_days,
        extra_ticket_price: config.extra_ticket_price,
        plans: config.plans
      });
      toast.success('Configurazione abbonamenti salvata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInstructions = async () => {
    if (readOnly) {
      toast.error('Solo gli utenti root possono modificare le istruzioni');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.updateTokenInstructions(instructions);
      toast.success('Istruzioni token aggiornate');
    } catch (err) {
      toast.error('Errore nel salvataggio istruzioni');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="subscription-config-title">
            <CreditCard className="w-8 h-8 inline mr-3 text-blue-500" />
            Piani Abbonamento
          </h1>
          <p className="text-muted-foreground mt-1">
            {readOnly 
              ? 'Visualizza i piani di abbonamento configurati' 
              : 'Configura i piani, i prezzi e le istruzioni per i token'}
          </p>
        </div>
        {readOnly && (
          <Badge variant="outline" className="flex items-center gap-1.5 text-amber-600 border-amber-300 bg-amber-50">
            <Eye className="w-3.5 h-3.5" />
            Solo lettura
          </Badge>
        )}
      </div>

      {/* Trial Period */}
      <TrialPeriodCard 
        config={config} 
        setConfig={setConfig} 
        readOnly={readOnly}
      />

      {/* Extra Ticket Price */}
      <ExtraTicketPriceCard 
        config={config} 
        setConfig={setConfig} 
        readOnly={readOnly}
      />

      {/* Subscription Plans */}
      <SubscriptionPlansCard 
        config={config} 
        setConfig={setConfig} 
        onSave={handleSaveConfig} 
        saving={saving}
        readOnly={readOnly}
      />

      {/* Token Instructions */}
      <TokenInstructionsCard 
        instructions={instructions} 
        setInstructions={setInstructions} 
        onSave={handleSaveInstructions} 
        saving={saving}
        readOnly={readOnly}
      />
    </div>
  );
}
